import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { inviteAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { GanttChart, Loader2, CheckCircle, AlertCircle, Users } from 'lucide-react';

export default function AcceptInvite() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [projectInfo, setProjectInfo] = useState(null);

  useEffect(() => {
    if (!token) return;
    const key = `invite_viewed_${token}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    inviteAPI.trackInviteView(token).catch(() => {});
  }, [token]);

  const handleAcceptInvite = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const res = await inviteAPI.acceptInvite(token);
      setSuccess(true);
      setProjectInfo({
        id: res.data.project_id,
        name: res.data.project_name
      });
      toast.success('Invitation accepted!');
    } catch (err) {
      const errorDetail = err.response?.data?.detail || 'Failed to accept invitation';
      console.error('Accept invite error:', err.response?.status, errorDetail);
      
      // Provide more specific error messages
      let userFriendlyError = errorDetail;
      if (errorDetail.includes('Invalid or expired')) {
        userFriendlyError = 'This invitation link was not found. Please ask the project owner to click "Resend" and use the newest email.';
      } else if (errorDetail.includes('different email')) {
        userFriendlyError = `This invitation was sent to a different email address. Please sign in with the email address the invitation was sent to.`;
      }
      
      setError(userFriendlyError);
      toast.error(userFriendlyError);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h1 className="font-outfit text-3xl font-bold text-zinc-900 mb-2">
            Invalid Invitation
          </h1>
          <p className="text-zinc-600 mb-8">
            This invitation link is invalid or has expired.
          </p>
          <Link to="/dashboard">
            <Button className="w-full h-12 bg-primary hover:bg-orange-600 text-white rounded-xl">
              Go to Dashboard
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (authLoading) {
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!user) {
    // URL encode the redirect path to handle special characters in token
    const redirectPath = encodeURIComponent(`/accept-invite?token=${token}`);
    
    return (
      <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
        <div className="w-full max-w-md text-center">
          <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Users className="w-8 h-8 text-orange-600" />
          </div>
          <h1 className="font-outfit text-3xl font-bold text-zinc-900 mb-2">
            You're Invited!
          </h1>
          <p className="text-zinc-600 mb-8">
            Please sign in or create an account to accept this project invitation.
          </p>
          <div className="space-y-3">
            <Link to={`/login?redirect=${redirectPath}`}>
              <Button data-testid="sign-in-btn" className="w-full h-12 bg-primary hover:bg-orange-600 text-white rounded-xl">
                Sign In
              </Button>
            </Link>
            <Link to={`/register?redirect=${redirectPath}`}>
              <Button data-testid="create-account-btn" variant="outline" className="w-full h-12 rounded-xl">
                Create Account
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
              <GanttChart className="w-6 h-6 text-white" />
            </div>
            <span className="font-outfit font-bold text-2xl text-zinc-900">ProjectOS</span>
          </Link>
        </div>

        {success ? (
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="font-outfit text-3xl font-bold text-zinc-900 mb-2">
              Welcome to the Team!
            </h1>
            <p className="text-zinc-600 mb-8">
              You've successfully joined <strong>{projectInfo?.name}</strong>.
            </p>
            <Button 
              onClick={() => navigate(`/project/${projectInfo?.id}`)}
              data-testid="open-project-btn"
              className="w-full h-12 bg-primary hover:bg-orange-600 text-white rounded-xl"
            >
              Open Project
            </Button>
          </div>
        ) : error ? (
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="font-outfit text-3xl font-bold text-zinc-900 mb-2">
              Invitation Error
            </h1>
            <p className="text-zinc-600 mb-8">{error}</p>
            <Link to="/dashboard">
              <Button data-testid="go-to-dashboard-btn" className="w-full h-12 bg-primary hover:bg-orange-600 text-white rounded-xl">
                Go to Dashboard
              </Button>
            </Link>
          </div>
        ) : (
          <div className="text-center">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Users className="w-8 h-8 text-orange-600" />
            </div>
            <h1 className="font-outfit text-3xl font-bold text-zinc-900 mb-2">
              Project Invitation
            </h1>
            <p className="text-zinc-600 mb-2">
              Signed in as <strong>{user.email}</strong>
            </p>
            <p className="text-zinc-600 mb-8">
              Click below to accept the invitation and join the project.
            </p>
            <Button 
              onClick={handleAcceptInvite}
              disabled={loading}
              data-testid="accept-invitation-btn"
              className="w-full h-12 bg-primary hover:bg-orange-600 text-white rounded-xl"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Accept Invitation'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
